import { useState, useEffect } from 'react';
import { Shield, Loader2, LogOut } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { adminLogin, isAdminAuthed, clearAdminToken } from '@/lib/adminAuth';

export default function AdminLogin() {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [authed, setAuthed] = useState(isAdminAuthed());

  useEffect(() => { setAuthed(isAdminAuthed()); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminLogin(password);
      setAuthed(true);
      toast.success('Admin session started');
      setPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const logout = () => { clearAdminToken(); setAuthed(false); toast.success('Logged out'); };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4" data-testid="admin-login-page">
      <div className="w-full max-w-sm bg-slate-900/60 border border-slate-800/60 rounded-2xl p-8">
        <Shield className="w-10 h-10 text-cyan-400 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-white text-center mb-1">Federation Admin</h1>
        <p className="text-sm text-slate-500 text-center mb-6">Restricted access — password required</p>
        {authed ? (
          <div className="text-center space-y-4" data-testid="admin-authed">
            <p className="text-sm text-emerald-400">You are signed in as admin.</p>
            <Button variant="outline" onClick={logout} className="gap-2 border-slate-700" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4" /> Log out
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <Input type="password" placeholder="Admin password" value={password}
              onChange={e => setPassword(e.target.value)} autoFocus
              className="bg-slate-950/60 border-slate-800" data-testid="admin-password-input" />
            <Button type="submit" disabled={loading || !password}
              className="w-full bg-cyan-600 hover:bg-cyan-500 gap-2" data-testid="admin-login-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
